/**
 * EndingsGalleryScene — grid of all 14 endings with seen/locked status (MY-1988).
 *
 * Layout (1280×720):
 *   Header: title + "X/14 DISCOVERED" counter
 *   Left:   3-column grid of ending cards (seen or locked silhouette)
 *   Right:  detail panel for the selected card
 *   Footer: key legend
 *
 * Navigation: arrow keys move the cursor; ESC/Backspace returns to TitleScene;
 * F jumps to FinaleChapterSelectScene (requires post-first-clear save).
 */

import Phaser from 'phaser';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import {
  buildGalleryEntries,
  galleryCounter,
  lockedCardHint,
  type GalleryEntry,
} from '@/systems/endingsGallery';
import { isFinaleChapterSelectAvailable } from '@/systems/finaleChapterSelect';

// ── Layout constants ────────────────────────────────────────────────────────

const COLS = 3;
const CARD_W = 260;
const CARD_H = 92;
const CARD_GAP_X = 14;
const CARD_GAP_Y = 10;
const GRID_X = 20;
const GRID_Y = 108;
// Panel sits to the right of the 3-column grid
const PANEL_X = GRID_X + COLS * CARD_W + (COLS - 1) * CARD_GAP_X + 22;
const PANEL_W = 1280 - 20 - PANEL_X;
const CARD_ROWS = 5; // ceil(14 / 3)
const PANEL_H = CARD_ROWS * CARD_H + (CARD_ROWS - 1) * CARD_GAP_Y;

// ── Fonts ───────────────────────────────────────────────────────────────────

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

// ── Tone accent colours ──────────────────────────────────────────────────────

const TONE_COLOURS: Record<string, number> = {
  Lawful: 0x4cc9f0,
  Heroic: 0x4ade80,
  Mercy: 0xa8edea,
  Dark: 0xef233c,
  Comedic: 0xffba08,
  Grey: 0x888888,
  Satirical: 0xff6b35,
  'Dark/Ambiguous': 0xb5179e,
  Nihilist: 0x666666,
  Pragmatic: 0x9ea4a9,
  Symbolic: 0xd4c5f9,
  Cold: 0x90e0ef,
  True: 0xffd700,
  'Secret/Sequel': 0xff0a54,
};

function toneColour(tone: string): number {
  return TONE_COLOURS[tone] ?? 0xaaaaaa;
}

function toneHex(tone: string): string {
  return `#${toneColour(tone).toString(16).padStart(6, '0')}`;
}

// ── Init data ────────────────────────────────────────────────────────────────

export interface EndingsGalleryInitData {
  slotIndex?: number;
}

// ── Scene ────────────────────────────────────────────────────────────────────

export class EndingsGalleryScene extends Phaser.Scene {
  private entries: GalleryEntry[] = [];
  private selectedIndex = 0;
  private prevIndex = 0;
  private slotIndex = ACTIVE_SLOT;
  private hasFinale = false;

  private cards: Phaser.GameObjects.Container[] = [];
  private detailContent!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'EndingsGalleryScene' });
  }

  init(data: EndingsGalleryInitData): void {
    this.slotIndex = data.slotIndex ?? ACTIVE_SLOT;
    this.selectedIndex = 0;
    this.prevIndex = 0;
  }

  create(): void {
    const { width, height } = this.scale;
    const save = getSaveManager().load(this.slotIndex);
    this.entries = buildGalleryEntries(save?.endingsSeen ?? []);
    this.hasFinale = isFinaleChapterSelectAvailable(save);

    this.drawBackground(width, height);
    this.buildHeader(width);
    this.buildGrid();
    this.buildDetailArea();
    this.buildFooter(width, height);
    this.syncSelection(true);
    this.bindKeys();
  }

  // ── Background ──────────────────────────────────────────────────────────────

  private drawBackground(width: number, height: number): void {
    this.add.rectangle(width / 2, height / 2, width, height, 0x080c12);
    // Subtle gradient via two overlapping rects
    this.add.rectangle(width / 2, height * 0.25, width, height * 0.5, 0x0d1520).setAlpha(0.5);
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  private buildHeader(width: number): void {
    this.add
      .text(width / 2, 28, 'ENDINGS GALLERY', {
        fontFamily: FONT_IMPACT,
        fontSize: '42px',
        color: '#ef233c',
        stroke: '#080c12',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    const counter = galleryCounter(this.entries);
    this.add
      .text(width / 2, 68, `${counter.seen} / ${counter.total} DISCOVERED`, {
        fontFamily: FONT_MONO,
        fontSize: '18px',
        color: '#ffba08',
        letterSpacing: 2,
      })
      .setOrigin(0.5);
  }

  // ── Card grid ───────────────────────────────────────────────────────────────

  private buildGrid(): void {
    this.cards = [];
    for (let i = 0; i < this.entries.length; i++) {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = GRID_X + col * (CARD_W + CARD_GAP_X);
      const cy = GRID_Y + row * (CARD_H + CARD_GAP_Y);
      const container = this.add.container(cx, cy);
      this.cards.push(container);
    }
  }

  private drawCard(
    container: Phaser.GameObjects.Container,
    entry: GalleryEntry,
    selected: boolean,
  ): void {
    container.removeAll(true);

    const bg = this.add.graphics();
    const fillCol = entry.seen ? 0x1a2235 : 0x0f1218;
    const borderCol = selected ? 0xffba08 : entry.seen ? 0x2a3a50 : 0x1a1e28;
    bg.fillStyle(fillCol, 1);
    bg.fillRect(0, 0, CARD_W, CARD_H);
    bg.lineStyle(selected ? 2 : 1, borderCol, 1);
    bg.strokeRect(0, 0, CARD_W, CARD_H);
    container.add(bg);

    if (entry.seen) {
      // Left accent bar
      const accent = this.add.graphics();
      accent.fillStyle(toneColour(entry.ending.tone), 0.9);
      accent.fillRect(0, 0, 4, CARD_H);
      container.add(accent);

      container.add(
        this.add.text(14, 8, `#${entry.displayIndex}`, {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: '#555e6a',
        }),
      );
      container.add(
        this.add.text(14, 24, entry.ending.title.toUpperCase(), {
          fontFamily: FONT_MONO,
          fontSize: '13px',
          color: selected ? '#ffffff' : '#c8d0da',
          wordWrap: { width: CARD_W - 20 },
        }),
      );
      container.add(
        this.add.text(14, CARD_H - 18, entry.ending.tone, {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: toneHex(entry.ending.tone),
        }),
      );
    } else {
      container.add(
        this.add.text(10, 8, `#${entry.displayIndex}`, {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: '#2e3540',
        }),
      );
      container.add(
        this.add
          .text(CARD_W / 2, CARD_H / 2 - 6, '???', {
            fontFamily: FONT_MONO,
            fontSize: '20px',
            color: selected ? '#4a5568' : '#242a35',
          })
          .setOrigin(0.5),
      );
      const hint = lockedCardHint(entry);
      if (hint) {
        container.add(
          this.add
            .text(CARD_W / 2, CARD_H - 10, hint, {
              fontFamily: FONT_MONO,
              fontSize: '9px',
              color: selected ? '#555e6a' : '#2e3540',
              wordWrap: { width: CARD_W - 12 },
              align: 'center',
            })
            .setOrigin(0.5, 1),
        );
      }
    }
  }

  // ── Detail panel ────────────────────────────────────────────────────────────

  private buildDetailArea(): void {
    // Static panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x0d1218, 1);
    bg.fillRect(PANEL_X, GRID_Y, PANEL_W, PANEL_H);
    bg.lineStyle(1, 0x1e2838, 1);
    bg.strokeRect(PANEL_X, GRID_Y, PANEL_W, PANEL_H);

    // Dynamic content lives in a container positioned at panel origin
    this.detailContent = this.add.container(PANEL_X, GRID_Y);
  }

  private updateDetailPanel(): void {
    this.detailContent.removeAll(true);

    const entry = this.entries[this.selectedIndex];
    if (!entry) return;

    const pad = 16;

    if (entry.seen) {
      // Tone accent bar
      const accent = this.add.graphics();
      accent.fillStyle(toneColour(entry.ending.tone), 0.9);
      accent.fillRect(0, 0, 4, PANEL_H);
      this.detailContent.add(accent);

      this.detailContent.add(
        this.add.text(pad, 14, `ENDING #${entry.displayIndex}`, {
          fontFamily: FONT_MONO,
          fontSize: '12px',
          color: '#555e6a',
        }),
      );
      this.detailContent.add(
        this.add.text(pad, 34, entry.ending.title.toUpperCase(), {
          fontFamily: FONT_IMPACT,
          fontSize: '22px',
          color: '#ffffff',
          wordWrap: { width: PANEL_W - pad * 2 },
        }),
      );
      this.detailContent.add(
        this.add.text(pad, 68, entry.ending.tone.toUpperCase(), {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: toneHex(entry.ending.tone),
          letterSpacing: 2,
        }),
      );
      this.detailContent.add(
        this.add.text(pad, 90, entry.ending.description, {
          fontFamily: FONT_MONO,
          fontSize: '14px',
          color: '#9ea4a9',
          wordWrap: { width: PANEL_W - pad * 2 },
          lineSpacing: 4,
        }),
      );
      // Per-ending reward stub
      // TODO(MY-1988): surface per-ending concept art / alt palette reward here
      this.detailContent.add(
        this.add.text(pad, PANEL_H - 36, '✦ REWARD: Concept art unlocked', {
          fontFamily: FONT_MONO,
          fontSize: '11px',
          color: '#ffba08',
        }),
      );
    } else {
      this.detailContent.add(
        this.add
          .text(PANEL_W / 2, PANEL_H / 2 - 44, `#${entry.displayIndex}`, {
            fontFamily: FONT_IMPACT,
            fontSize: '56px',
            color: '#1a2030',
          })
          .setOrigin(0.5),
      );
      this.detailContent.add(
        this.add
          .text(PANEL_W / 2, PANEL_H / 2 + 14, 'NOT YET DISCOVERED', {
            fontFamily: FONT_MONO,
            fontSize: '14px',
            color: '#2e3848',
            letterSpacing: 2,
          })
          .setOrigin(0.5),
      );
      const hint = lockedCardHint(entry);
      if (hint) {
        this.detailContent.add(
          this.add
            .text(PANEL_W / 2, PANEL_H / 2 + 44, hint, {
              fontFamily: FONT_MONO,
              fontSize: '12px',
              color: '#3a4860',
              wordWrap: { width: PANEL_W - 32 },
              align: 'center',
            })
            .setOrigin(0.5),
        );
      }
    }
  }

  // ── Footer ──────────────────────────────────────────────────────────────────

  private buildFooter(width: number, height: number): void {
    const legend = this.hasFinale
      ? '↑↓←→ NAVIGATE   [F] FINALE REPLAY   [ESC] BACK'
      : '↑↓←→ NAVIGATE   [ESC] BACK';

    this.add
      .text(width / 2, height - 16, legend, {
        fontFamily: FONT_MONO,
        fontSize: '12px',
        color: '#3a3f4a',
        letterSpacing: 1,
      })
      .setOrigin(0.5);
  }

  // ── Selection sync ──────────────────────────────────────────────────────────

  /**
   * Redraw changed cards and refresh the detail panel.
   * On first call (init=true) rebuild all cards.
   */
  private syncSelection(init = false): void {
    if (init) {
      for (let i = 0; i < this.entries.length; i++) {
        const container = this.cards[i];
        if (container) this.drawCard(container, this.entries[i]!, i === this.selectedIndex);
      }
    } else {
      const prev = this.cards[this.prevIndex];
      if (prev) this.drawCard(prev, this.entries[this.prevIndex]!, false);
      const next = this.cards[this.selectedIndex];
      if (next) this.drawCard(next, this.entries[this.selectedIndex]!, true);
    }
    this.updateDetailPanel();
  }

  // ── Input ────────────────────────────────────────────────────────────────────

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    const up = kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    const down = kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);
    const left = kb.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
    const right = kb.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
    const esc = kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
    const backspace = kb.addKey(Phaser.Input.Keyboard.KeyCodes.BACKSPACE);
    const fKey = kb.addKey(Phaser.Input.Keyboard.KeyCodes.F);

    up.on('down', () => this.move(-COLS));
    down.on('down', () => this.move(COLS));
    left.on('down', () => this.move(-1));
    right.on('down', () => this.move(1));
    esc.on('down', () => this.scene.start('TitleScene'));
    backspace.on('down', () => this.scene.start('TitleScene'));
    fKey.on('down', () => this.onFinaleReplay());
  }

  private move(delta: number): void {
    const next = this.selectedIndex + delta;
    if (next >= 0 && next < this.entries.length) {
      this.prevIndex = this.selectedIndex;
      this.selectedIndex = next;
      this.syncSelection(false);
    }
  }

  private onFinaleReplay(): void {
    if (this.hasFinale) {
      this.scene.start('FinaleChapterSelectScene', { slotIndex: this.slotIndex });
    }
  }
}
